import React from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, MailCheck, UserPlus } from 'lucide-react';
import type { EmailOtpType } from '@supabase/supabase-js';
import { AuthPageLayout } from '../components/AuthPageLayout';
import { supabase } from '../lib/supabaseClient';

const OTP_TYPES: readonly EmailOtpType[] = ['invite', 'magiclink', 'recovery', 'signup', 'email', 'email_change'];

function parseOtpType(raw: string | null): EmailOtpType | null {
  return OTP_TYPES.find((t) => t === raw) ?? null;
}

function isExpiredMessage(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('expired') || m.includes('invalid') || m.includes('not found');
}

/**
 * Landing page for links in invite / login / password-reset emails.
 * The one-time token is only redeemed when the user clicks the button, so mail
 * security scanners that pre-open links cannot use it up.
 */
export function AuthConfirmPage() {
  const navigate = useNavigate();
  const params = React.useMemo(() => new URLSearchParams(window.location.search), []);
  const tokenHash = params.get('token_hash')?.trim() ?? '';
  const type = parseOtpType(params.get('type'));
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const content =
    type === 'invite'
      ? {
          icon: UserPlus,
          title: 'Einladung annehmen',
          text: 'Du wurdest eingeladen. Klicke auf den Button, um deinen Zugang einzurichten.',
          button: 'Einladung annehmen',
        }
      : type === 'recovery'
        ? {
            icon: KeyRound,
            title: 'Passwort zurücksetzen',
            text: 'Klicke auf den Button, um ein neues Passwort festzulegen.',
            button: 'Weiter zum neuen Passwort',
          }
        : {
            icon: MailCheck,
            title: 'Anmeldung bestätigen',
            text: 'Klicke auf den Button, um dich anzumelden.',
            button: 'Jetzt anmelden',
          };
  const Icon = content.icon;

  const handleConfirm = async () => {
    if (!tokenHash || !type) return;
    setError(null);
    setLoading(true);
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
      if (verifyError) {
        if (isExpiredMessage(verifyError.message)) {
          navigate('/auth/link-expired', { replace: true });
          return;
        }
        throw new Error(verifyError.message);
      }
      if (type === 'recovery') {
        navigate('/auth/reset-password', { replace: true });
        return;
      }
      // AuthCallbackPage picks up the new session and runs the usual post-login flow.
      navigate('/auth/callback', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bestätigung fehlgeschlagen.');
      setLoading(false);
    }
  };

  if (!tokenHash || !type) {
    return (
      <AuthPageLayout>
        <h1 className="text-2xl font-bold text-slate-900">Link unvollständig</h1>
        <p className="text-sm text-slate-500 mt-2 leading-relaxed">
          Dieser Link ist unvollständig. Bitte öffne den Link direkt aus der E-Mail oder lass dir einen neuen schicken.
        </p>
        <button
          type="button"
          onClick={() => navigate('/auth/link-expired', { replace: true })}
          className="w-full mt-6 rounded-xl bg-[#3b82f6] px-4 py-3 text-sm font-semibold text-white hover:bg-blue-600 transition-colors shadow-sm"
        >
          Neuen Link anfordern
        </button>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <div className="flex flex-col items-center text-center lg:items-start lg:text-left gap-4">
        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
          <Icon className="w-7 h-7 text-[#3b82f6]" aria-hidden />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{content.title}</h1>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed">{content.text}</p>
        </div>
      </div>

      {error ? (
        <div className="mt-6 rounded-xl bg-red-50 border border-red-200 px-3.5 py-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => { void handleConfirm(); }}
        disabled={loading}
        className="w-full mt-6 rounded-xl bg-[#3b82f6] px-4 py-3 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-60 transition-colors shadow-sm"
      >
        {loading ? 'Wird bestätigt…' : content.button}
      </button>

      <button
        type="button"
        onClick={() => navigate('/login', { replace: true })}
        className="w-full mt-4 text-sm text-[#3b82f6] hover:underline"
      >
        Zurück zum Login
      </button>
    </AuthPageLayout>
  );
}
